import type { AuthorizationUser, RoleAssignment } from "@/api/authorization";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { AccountMetadata } from "./AccountMetadata";

export function SelectedUserContext({
  user,
  timeZone,
}: {
  user?: AuthorizationUser;
  timeZone?: string;
}) {
  if (!user) return null;
  return (
    <div className="min-w-0 break-words" aria-label="Selected user account">
      <p>
        {user.first_name} {user.last_name}
      </p>
      <p className="text-sm text-slate-400">{user.email}</p>
      <AccountMetadata
        status={user.is_active ? "ACTIVE" : "DISABLED"}
        lastLoginAt={user.last_login_at}
        timeZone={timeZone}
      />
    </div>
  );
}

export function AssignmentAccessCard({
  assignment,
  user,
  timeZone,
  onToggle,
  onEditScope,
  nested = false,
  pending = false,
}: {
  nested?: boolean;
  pending?: boolean;
  assignment: RoleAssignment;
  user?: AuthorizationUser;
  timeZone?: string;
  onToggle: () => void;
  onEditScope?: () => void;
}) {
  return (
    <Card className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 break-words">
        {!nested && <h2 className="font-semibold">
          {assignment.user_name || assignment.user_email}
        </h2>}
        <p className="text-sm text-slate-300">{assignment.role_name}</p>
        <p className="text-xs text-slate-500">
          {assignment.ministry_name
            ? `Ministry scope: ${assignment.ministry_name}`
            : "Entire church scope"}
        </p>
        <div className="mt-2">
          <Badge variant={assignment.is_active ? "success" : "neutral"}>
            Assignment: {assignment.is_active ? "Active" : "Disabled"}
          </Badge>
        </div>
        {!nested && (user ? (
          <AccountMetadata
            status={user.is_active ? "ACTIVE" : "DISABLED"}
            lastLoginAt={user.last_login_at}
            timeZone={timeZone}
          />
        ) : (
          <p className="mt-2 text-sm text-slate-400">
            Account information unavailable.
          </p>
        ))}
      </div>
      <div className="flex gap-2">
      {onEditScope && <Button variant="secondary" size="sm" disabled={pending} onClick={onEditScope}>Edit scope</Button>}
      <Button
        variant={assignment.is_active ? "danger" : "secondary"}
        size="sm"
        disabled={pending}
        onClick={onToggle}
      >
        {assignment.is_active ? "Disable" : "Restore"}
      </Button>
      </div>
    </Card>
  );
}
